import { List, Map, OrderedSet } from "immutable";
import moment from "moment";
import { createSelector } from "reselect";

import { ContentType } from "@seneca/course-service-types";
import { XpEventData } from "@seneca/xp-service-types";

import { MAX_SESSION_DURATION } from "seneca-common/constants";
import {
  Content,
  getAllSectionContentIdsFromSections,
  getContentModuleIds
} from "seneca-common/features/course/state";
import SmartLearningSuggestionReason from "seneca-common/features/smart-learning/models/SuggestionReason";
import {
  ContentStat,
  getContentStats,
  getSectionModuleIdsIncorrectLast,
  hasSectionStat
} from "seneca-common/features/stats/state";
import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import { priorityFilter } from "../../../utils/functions/priorityFilter";
import {
  contentIsExercise,
  getAllSectionExerciseContentIdsFromSections,
  getContent
} from "../../course/state/selectors/state";
import { PATH_TO_SESSIONS_STATE } from "../consts";
import {
  filterFirstModuleIfShown,
  getMinimumDifficulty
} from "../logic/session-types/exercise/utils";
import Session from "../models/Session";
import SessionItem, { SESSION_ITEM_TYPES } from "../models/SessionItem";
import SessionOptions from "../models/SessionOptions";
import { SESSION_TYPES, SessionType } from "../models/SessionType";
import { SessionXp } from "../models/SessionXp";
import {
  getCompleted,
  getContentId,
  getContentModuleId,
  getGaveUp,
  getModuleType,
  getScore,
  getSessionModule,
  getSessionModules,
  getTestingActive,
  wasCorrect,
  wasIncorrect
} from "./sessionModule";
import * as sessionsSelectors from "./state/sessions";
import * as sessionSelectors from "./state/sessions/session";

export function getSessions(state: any): Map<string, Session> {
  return state.get(PATH_TO_SESSIONS_STATE);
}

export function getSessionIds(state: any): List<string> {
  return state.get("sessions").keySeq().toList();
}

export const getCompletedSessions: (state: any) => Map<string, Session> =
  createSelector(getSessions, sessionsSelectors.getCompletedSessions);

export const getLastSession: (state: any) => Session | null | undefined =
  createSelector(getSessions, sessionsSelectors.getLastSession);

export const getNumberOfCompletedSessions = createSelector(
  getSessions,
  sessionsSelectors.getNumberOfCompletedSessions
);

export function getNumberOfSessions(state: any): number {
  return getSessionIds(state).size;
}

export function getSession(state: any, sessionId: string) {
  return getSessions(state).get(sessionId);
}

export const getSessionType: (
  arg0: any,
  arg1: string
) => SessionType | null | undefined = composeSelector(
  getSession,
  sessionSelectors.getSessionType
);

export const getSessionOptions: (
  state: any,
  sessionId: string
) => SessionOptions | undefined = composeSelector(
  getSession,
  sessionSelectors.getSessionOptions
);

export function getSessionCourseId(state: any, sessionId: string) {
  return getSession(state, sessionId)!.courseId;
}

export function getSessionCompleted(state: any, sessionId: string): boolean {
  const session = getSession(state, sessionId);
  return !!session && sessionSelectors.getCompleted(session);
}

export function getSessionStartTime(state: any, sessionId: string) {
  return getSession(state, sessionId)!.startTime;
}

export function getSessionWonMemory(state: any, sessionId: string): boolean {
  return !!getSession(state, sessionId)!.wonMemory;
}

export function completesAssignment(state: any, sessionId: string) {
  return getSession(state, sessionId)!.completesAssignment;
}

export function getSessionItems(state: any, sessionId: string) {
  return getSession(state, sessionId)!.items;
}

export function getSessionItemAtIndex(
  state: any,
  sessionId: string,
  index: number
) {
  return getSessionItems(state, sessionId).get(index);
}
export function getIdOfSessionItemAtIndex(
  state: any,
  sessionId: string,
  index: number
) {
  return getSessionItemAtIndex(state, sessionId, index)!.id;
}

export function getSessionModulesInOrder(
  state: any,
  sessionId: string
): List<SessionItem> {
  const sessionItems = getSessionItems(state, sessionId);
  return sessionItems.filter(
    (item: any) => item.type === SESSION_ITEM_TYPES.MODULE
  );
}

export function getSessionModuleIds(state: any, sessionId: string) {
  return getSessionModulesInOrder(state, sessionId).map(module => module.id!);
}

export function getTestedSessionModuleIds(state: any, sessionId: string) {
  return getSessionModuleIds(state, sessionId).filter(sessionModuleId =>
    getTestingActive(state, sessionId, sessionModuleId)
  );
}

export function getItemOrderInSession(
  state: any,
  sessionId: string,
  id: string
): number {
  return getSessionItems(state, sessionId)
    .map((item: any) => item.id)
    .indexOf(id);
}

export function getModuleOrderInSession(
  state: any,
  sessionId: string,
  id: string
): number {
  return getSessionModulesInOrder(state, sessionId)
    .map(module => module.id)
    .indexOf(id);
}

export function getCompletedExerciseModuleIds(
  state: any,
  sessionId: string
): List<string | undefined> {
  return getSessionModuleIds(state, sessionId).filter(
    sessionModuleId =>
      contentIsExercise(
        state,
        getContentId(state, sessionId, sessionModuleId!)
      ) && getCompleted(state, sessionId, sessionModuleId)
  );
}

export function getNumberOfSessionModules(
  state: any,
  sessionId: string
): number {
  return getSessionModuleIds(state, sessionId).size;
}

export function getNumberOfSessionItems(state: any, sessionId: string): number {
  return getSessionItems(state, sessionId).size;
}

export function sessionItemIsModule(
  state: any,
  sessionId: string,
  sessionItemId: string
): boolean {
  const itemIndex = getItemOrderInSession(state, sessionId, sessionItemId);
  const item = getSessionItemAtIndex(state, sessionId, itemIndex);
  return item!.type === SESSION_ITEM_TYPES.MODULE;
}

export const isStartOfFirstSession = (state: any, sessionId: string): boolean =>
  getNumberOfSessions(state) === 1 &&
  getNumberOfSessionItems(state, sessionId) === 0;

export function getNumberOfModulesTested(
  state: any,
  sessionId: string
): number {
  return getSessionModuleIds(state, sessionId).count(sessionModuleId =>
    getTestingActive(state, sessionId, sessionModuleId)
  );
}

export function getNumberOfCorrectModules(
  state: any,
  sessionId: string
): number {
  return getSessionModuleIds(state, sessionId).count(sessionModuleId =>
    wasCorrect(state, sessionId, sessionModuleId)
  );
}

export function getNumberOfIncorrectModules(
  state: any,
  sessionId: string
): number {
  return getSessionModuleIds(state, sessionId).count(sessionModuleId =>
    wasIncorrect(state, sessionId, sessionModuleId)
  );
}

export function getNumberOfGaveUpModules(
  state: any,
  sessionId: string
): number {
  return getSessionModuleIds(state, sessionId).count(sessionModuleId =>
    getGaveUp(state, sessionId, sessionModuleId)
  );
}

export function getLastItemIndex(state: any, sessionId: string): number {
  return getSessionItems(state, sessionId).size - 1;
}

export function getContentIdOfLastModule(
  state: any,
  sessionId: string
): string {
  const sessionModuleId = getSessionModuleIds(state, sessionId).last();
  return getContentId(state, sessionId, sessionModuleId!);
}

export function getContentModuleIdOfLastModule(
  state: any,
  sessionId: string
): string {
  const sessionModuleId = getSessionModuleIds(state, sessionId).last();
  return getContentModuleId(state, sessionId, sessionModuleId);
}

export function getModuleTypeOfLastModule(
  state: any,
  sessionId: string
): string {
  const sessionModuleId = getSessionModuleIds(state, sessionId).last();
  return getModuleType(state, sessionId, sessionModuleId);
}

export function getSessionSectionIds(
  state: any,
  sessionId: string
): List<string> {
  return (
    (getSession(state, sessionId) &&
      getSession(state, sessionId)!.sectionIds) ||
    List()
  );
}

export function getSessionWordIds(state: any, sessionId: string): List<string> {
  return getSession(state, sessionId)!.wordIds || List();
}

export function getSessionModuleIdsWhitelist(
  state: any,
  sessionId: string
): List<string> {
  return (
    (getSession(state, sessionId) &&
      getSession(state, sessionId)!.moduleIdsWhitelist) ||
    List()
  );
}

export const getSessionSectionId = (state: any, sessionId: string): string => {
  // this may change in future - making an assumption that session associated with one section at sub topic level
  const sectionIds = getSessionSectionIds(state, sessionId);
  return sectionIds.get(0)!;
};

export function doesSessionIncludeSection(
  state: any,
  sessionId: string,
  sectionId: string
): boolean {
  return getSessionSectionIds(state, sessionId).includes(sectionId);
}

export function getSessionContentIds(
  state: any,
  sessionId: string
): List<string> {
  return getSession(state, sessionId)!.contentIds || List();
}

export function getAllSessionContentIds(
  state: any,
  sessionId: string
): List<string> {
  const hasHardestQuestionContent = getHasHardestQuestionContent(
    state,
    sessionId
  );

  const allContentIds = getAllContentIds(
    state,
    getSessionSectionIds(state, sessionId),
    getSessionContentIds(state, sessionId)
  );

  if (hasHardestQuestionContent) {
    const hardestContentIds = allContentIds.filter(contentId => {
      const content: Content = getContent(state, contentId);

      return content.contentType === ContentType.hardestQuestions;
    });

    return hardestContentIds;
  }

  const standardContentIds = allContentIds.filter(contentId => {
    const content: Content = getContent(state, contentId);

    return content.contentType === ContentType.standard;
  });
  return standardContentIds;
}

export function getAllSessionExerciseContentIds(
  state: any,
  sessionId: string
): List<string> {
  return getAllExerciseContentIds(
    state,
    getSessionSectionIds(state, sessionId)
  );
}

export function getAllContentIds(
  state: any,
  sectionIds: List<string> = List(),
  contentIds: List<string> = List()
): List<string> {
  const sectionContentIds = getAllSectionContentIdsFromSections(
    state,
    sectionIds
  );
  const allContentIds = contentIds.reduce(
    (contentIds, contentId) =>
      contentIds.contains(contentId) ? contentIds : contentIds.push(contentId),
    sectionContentIds
  );
  return allContentIds;
}

export function getAllExerciseContentIds(
  state: any,
  sectionIds: List<string> = List()
): List<string> {
  return getAllSectionExerciseContentIdsFromSections(state, sectionIds);
}

export function getSessionContentStats(
  state: any,
  sessionId: string
): Map<string, ContentStat> {
  return getContentStats(state, getAllSessionContentIds(state, sessionId));
}

export const getSessionEndTime = (state: any, sessionId: string) => {
  return getSession(state, sessionId)!.endTime;
};

function getSessionDuration(state: any, sessionId: string): number {
  const startTime = getSession(state, sessionId)!.startTime;
  const endTime = getSessionEndTime(state, sessionId);
  return moment(endTime!).diff(moment(startTime!));
}

/** @deprecated Use getCappedSessionDuration from sessionModules instead. */
export const getCappedSessionDuration = (
  state: any,
  sessionId: string
): number =>
  Math.min(getSessionDuration(state, sessionId), MAX_SESSION_DURATION);

export function getSessionStartingProficiency(state: any, sessionId: string) {
  return getSession(state, sessionId)!.startingProficiency;
}

export function getSessionEndingProficiency(state: any, sessionId: string) {
  return getSession(state, sessionId)!.endingProficiency;
}

export function getSessionStartingCourseProficiency(
  state: any,
  sessionId: string
) {
  return getSession(state, sessionId)!.startingCourseProficiency;
}

export function getSessionStartingBestScore(state: any, sessionId: string) {
  return getSession(state, sessionId)!.startingBestScore;
}

export function getSessionEndingCourseProficiency(
  state: any,
  sessionId: string
) {
  return getSession(state, sessionId)!.endingCourseProficiency;
}

export function getSessionEndingCourseScore(state: any, sessionId: string) {
  return getSession(state, sessionId)!.endingCourseScore;
}

export function calculateSessionAverageScore(
  state: any,
  sessionId: string
): number {
  return (
    getSessionModuleIds(state, sessionId).reduce(
      (total, sessionModuleId) =>
        getTestingActive(state, sessionId, sessionModuleId)
          ? (total += getScore(state, sessionId, sessionModuleId))
          : total,
      0
    ) / getNumberOfModulesTested(state, sessionId)
  );
}

export function calculateSessionScore(state: any, sessionId: string) {
  switch (getSessionType(state, sessionId)) {
    case SESSION_TYPES.wrongAnswers:
      return 1;
    case SESSION_TYPES.adaptive:
    default:
      const numberOfTestedModules = getNumberOfModulesTested(state, sessionId);
      return numberOfTestedModules === 0
        ? 1
        : calculateSessionAverageScore(state, sessionId);
  }
}

export function getAllStudiedCourseIdsFromSessions(state: any): List<string> {
  return getSessionIds(state).reduce((courseIds, sessionId) => {
    return courseIds.push(getSessionCourseId(state, sessionId)!);
  }, List());
}

export function getExerciseContentMinimumDifficulty(
  state: any,
  sessionId: string
): number {
  const sessionContentIds = getAllSessionExerciseContentIds(state, sessionId);
  const contentId = sessionContentIds.first()!;

  let contentModuleIds: List<string> = getContentModuleIds(state, contentId);
  const sessionModuleIds = getSessionModuleIds(state, sessionId);
  contentModuleIds = priorityFilter(contentModuleIds, [
    filterFirstModuleIfShown(contentModuleIds.first(), sessionModuleIds)
  ]);

  return getMinimumDifficulty(state, contentModuleIds);
}

export function getWrongSessionModuleIds(
  state: any,
  sessionId: string
): List<string> {
  const sessionType = getSessionType(state, sessionId);
  if (sessionType === SESSION_TYPES.wrongAnswers) {
    return List();
  }

  const sectionId = getSessionSectionId(state, sessionId);

  const previousSectionWrongModuleIds =
    sessionType !== SESSION_TYPES.multiSection &&
    hasSectionStat(state, sectionId)
      ? getSectionModuleIdsIncorrectLast(state, sectionId)
      : [];

  const sessionModules = getSessionModules(state, sessionId);

  if (!sessionModules) {
    return List();
  }

  const updatedSessionModules = sessionModules.reduce(
    (acc: any, module: any) => {
      if (!module.testingActive) {
        return acc;
      }
      if (module.score < 1) {
        return acc.add(module.contentModuleId);
      }
      if (module.score === 1 && acc.has(module.contentModuleId)) {
        return acc.remove(module.contentModuleId);
      }
      return acc;
    },
    // @ts-ignore
    OrderedSet(previousSectionWrongModuleIds)
  );

  return updatedSessionModules.toList();
}

const VOCABULARY_MODULE_MAX_ATTEMPTS = 2;

export function getVocabularySessionModuleIdsToRetry(
  state: any,
  sessionId: string
): List<string> {
  const sessionType = getSessionType(state, sessionId);
  if (sessionType !== SESSION_TYPES.vocabulary) {
    throw new Error("Session is not a vocabulary session");
  }

  const sessionModules = getSessionModules(state, sessionId);
  const sessionItems = getSessionItems(state, sessionId);

  if (!sessionItems) {
    return List();
  }

  const moduleIdsToRetry = sessionItems.reduce((acc: any, item) => {
    if (item.type !== SESSION_ITEM_TYPES.MODULE) {
      return acc;
    }

    const module = getSessionModule(state, sessionId, item.id);
    if (!module.testingActive) {
      return acc;
    }

    const numAttempts = sessionModules.filter(
      sessionModule => sessionModule.contentModuleId === module.contentModuleId
    ).size;

    if (
      (module.score === undefined || module.score < 1) &&
      numAttempts < VOCABULARY_MODULE_MAX_ATTEMPTS
    ) {
      return acc.add(module.contentModuleId);
    }
    return acc;
  }, OrderedSet());

  return moduleIdsToRetry.toList();
}

/** A completed module is either correct, or has been retried incorrectly */
export function getVocabularySessionCompletedModuleIds(
  state: any,
  sessionId: string
): List<string> {
  const sessionType = getSessionType(state, sessionId);
  if (sessionType !== SESSION_TYPES.vocabulary) {
    throw new Error("Session is not a vocabulary session");
  }

  const sessionModules = getSessionModules(state, sessionId);
  const sessionItems = getSessionItems(state, sessionId);

  if (!sessionItems) {
    return List();
  }

  const completedModuleIds = sessionItems.reduce((acc: any, item) => {
    if (item.type !== SESSION_ITEM_TYPES.MODULE) {
      return acc;
    }

    const module = getSessionModule(state, sessionId, item.id);

    const numAttempts = sessionModules.filter(
      sessionModule =>
        sessionModule.contentModuleId === module.contentModuleId &&
        sessionModule.completed
    ).size;

    if (
      !module.testingActive ||
      module.score === 1 ||
      numAttempts >= VOCABULARY_MODULE_MAX_ATTEMPTS
    ) {
      return acc.add(module.contentModuleId);
    }
    return acc;
  }, OrderedSet());

  return completedModuleIds.toList();
}

export function makeGetActiveSectionSessionId(
  sectionId: string
): (arg0: any) => string | null | undefined {
  return createSelector(getSessions, sessions =>
    sessionsSelectors.getActiveSectionSessionId(sessions, sectionId)
  );
}

export const getFocusedItemId: (
  arg0: any,
  arg1: string
) => string | null | undefined = composeSelector(
  getSession,
  sessionSelectors.getFocusedItemId
);

export const isFocusedItem: (arg0: any, arg1: string, arg2: string) => boolean =
  composeSelector(getSession, (session, sessionId, id) =>
    sessionSelectors.isFocusedItem(session!, id)
  );

export const isWrongAnswersSession: (arg0: any, arg1: string) => boolean =
  composeSelector(getSession, sessionSelectors.isWrongAnswersSession);

export const isQuizSession: (arg0: any, arg1: string) => boolean =
  composeSelector(getSession, sessionSelectors.isQuizSession);

export const getSessionScore: (
  arg0: any,
  arg1: string
) => number | null | undefined = composeSelector(
  getSession,
  sessionSelectors.getSessionScore
);

export const getSessionSmartLearningSuggestionReason = (
  state: any,
  sessionId: string
): SmartLearningSuggestionReason | undefined =>
  getSession(state, sessionId)!.smartLearningSuggestionReason;

export const isLastSessionModuleIncorrect = (
  state: any,
  sessionId: string
): boolean => {
  const moduleIds = getSessionModuleIds(state, sessionId);
  const lastModuleId = moduleIds && moduleIds.last();

  const testingActive =
    lastModuleId && getTestingActive(state, sessionId, lastModuleId);
  const incorrect =
    lastModuleId && wasIncorrect(state, sessionId, lastModuleId);
  return !!testingActive && !!incorrect;
};

export const getAreSessionModulesAnsweredCorrectly = (
  state: any,
  sessionId: string
): List<boolean> => {
  return getSessionModuleIds(state, sessionId).map(moduleId => {
    if (!moduleId) return false;

    const testingActive = getTestingActive(state, sessionId, moduleId);
    const incorrect = wasIncorrect(state, sessionId, moduleId);
    return !!testingActive && !incorrect;
  });
};

export const getAreSessionModulesAnswered = (
  state: any,
  sessionId: string
): List<boolean> => {
  return getSessionModuleIds(state, sessionId).map(moduleId => {
    if (!moduleId) return false;

    const testingActive = getTestingActive(state, sessionId, moduleId);
    const correct = wasCorrect(state, sessionId, moduleId);
    const incorrect = wasIncorrect(state, sessionId, moduleId);

    return !!testingActive && (incorrect || correct);
  });
};

export const getSessionXp: (
  state: any,
  sessionId: string
) => SessionXp | null | undefined = composeSelector(
  getSession,
  sessionSelectors.getXp
);

export const getSessionNewXp: (
  state: any,
  sessionId: string
) => number | undefined = composeSelector(
  getSession,
  sessionSelectors.getNewXp
);

export const getSessionXpBonusMultiplier: (
  state: any,
  sessionId: string
) => number | undefined = composeSelector(
  getSession,
  sessionSelectors.getXpBonusMultiplier
);

export const getSessionXpBreakdown: (
  state: any,
  sessionId: string
) => XpEventData["breakdown"] | undefined = composeSelector(
  getSession,
  sessionSelectors.getXpBreakdown
);

export const getNumberOfModulesBeforeMessage = (
  state: any,
  sessionId: string,
  itemId: string
) => {
  const sessionItems = getSessionItems(state, sessionId);

  const itemIndex = sessionItems.findIndex(item => item.id === itemId);

  const itemsBeforeMessage = sessionItems.slice(0, itemIndex);

  return itemsBeforeMessage.count(
    item => item.type === SESSION_ITEM_TYPES.MODULE
  );
};

export const getHasHardestQuestionContent: (
  state: any,
  sessionId: string
) => boolean | undefined = composeSelector(
  getSession,
  sessionSelectors.getHasHardestQuestionContent
);
